import React from 'react';
import { cn } from '../utils/cn';

export type ButtonVariant =
  | 'filled'
  | 'tonal'
  | 'outlined'
  | 'text'
  | 'elevated'
  | 'destructive'
  // legacy aliases for backwards compatibility
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'outline';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  filled:
    'bg-primary text-primary-foreground shadow-level-1 hover:bg-primary-hover hover:shadow-level-2 active:scale-[0.98]',
  tonal:
    'bg-primary-container text-primary-container-foreground hover:brightness-105 active:scale-[0.98]',
  outlined:
    'border border-outline bg-transparent text-foreground hover:bg-surface-variant active:scale-[0.98]',
  text: 'bg-transparent text-primary hover:bg-primary/10 active:scale-[0.98]',
  elevated:
    'bg-surface text-primary shadow-level-1 hover:shadow-level-2 hover:bg-surface-variant active:scale-[0.98]',
  destructive:
    'bg-destructive text-destructive-foreground shadow-level-1 hover:brightness-105 active:scale-[0.98]',
  // Legacy mappings
  primary:
    'bg-primary text-primary-foreground shadow-level-1 hover:bg-primary-hover hover:shadow-level-2 active:scale-[0.98]',
  secondary:
    'bg-secondary text-secondary-foreground hover:brightness-105 active:scale-[0.98]',
  danger:
    'bg-destructive text-destructive-foreground shadow-level-1 hover:brightness-105 active:scale-[0.98]',
  outline:
    'border border-outline bg-transparent text-foreground hover:bg-surface-variant active:scale-[0.98]',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-body-md gap-1.5',
  md: 'h-10 px-5 text-body-md gap-2',
  lg: 'h-12 px-6 text-body-lg gap-2',
  icon: 'h-10 w-10 p-0',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'filled',
      size = 'md',
      isLoading = false,
      loadingText,
      leftIcon,
      rightIcon,
      fullWidth,
      className,
      disabled,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || isLoading;
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          'inline-flex items-center justify-center rounded-lg font-medium whitespace-nowrap transition-all duration-2 ease-standard select-none',
          'focus-visible:outline-none focus-visible:shadow-focus-ring',
          'disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none disabled:hover:scale-100',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className,
        )}
        disabled={isDisabled}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading ? (
          <>
            <span
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
              aria-hidden
            />
            <span>{loadingText ?? 'در حال انجام...'}</span>
          </>
        ) : (
          <>
            {leftIcon ? (
              <span className="inline-flex shrink-0" aria-hidden>
                {leftIcon}
              </span>
            ) : null}
            <span>{children}</span>
            {rightIcon ? (
              <span className="inline-flex shrink-0" aria-hidden>
                {rightIcon}
              </span>
            ) : null}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
